import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useCalculatorAnalytics from '../hooks/useCalculatorAnalytics';
import BRAND from '../config/brand';
import CalculatorDiscoveryCta from './CalculatorDiscoveryCta';
import ServiceNextStep from './ServiceNextStep';
import { CONTENT_CATEGORIES } from '../config/planningOffers';
import './IncomeChangePaycheckTaxEstimator.css';
import './RothTraditionalCalculator.css';

const NOMINAL_RETURN_ACCUMULATION = 0.095;
const NOMINAL_RETURN_WITHDRAWAL = 0.07;
const INFLATION = 0.025;
const WITHDRAWAL_RATE = 0.04;
const INCOME_REPLACEMENT_RATE = 0.80;
const RMD_START_AGE = 75;
const CHART_END_AGE = 95;
const SS_CLAIM_AGE = 67;
const SS_TAXABLE_PORTION = 0.85;
const OVERSAVING_BALANCE_THRESHOLD = 500000;
// Only flag over-saving when current contributions exceed the minimum needed by this margin.
const OVERSAVING_HEADROOM_RATIO = 0.20;
// Ending balance tolerance when solving for a withdrawal rate that lasts to age 95.
const SUSTAINABLE_BALANCE_TOLERANCE = 25000;
// 2026 federal standard deduction (IRS Rev. Proc. 2025-32).
const STANDARD_DEDUCTION = { single: 16100, married: 32200 };

const DEFAULTS = {
  filingStatus: 'married',
  currentAge: '',
  retirementAge: '',
  householdIncome: '',
  annualContribution: '',
  spouseContribution: '',
  spouseAge: '',
  spouseRetirementAge: '',
  spouseIncome: '',
  taxableBalance: '',
  preTaxBalance: '',
  rothBalance: '',
};
const RMD_DIVISORS = {
  75: 24.6, 76: 23.7, 77: 22.9, 78: 22.0, 79: 21.1, 80: 20.2,
  81: 19.4, 82: 18.5, 83: 17.7, 84: 16.8, 85: 16.0, 86: 15.2,
  87: 14.4, 88: 13.7, 89: 12.9, 90: 12.2,
};

// 2026 federal ordinary-income brackets by taxable-income ceiling (IRS Rev. Proc. 2025-32).
const BRACKET_SCHEDULE = {
  single: [[12400, 0.1], [50400, 0.12], [105700, 0.22], [201775, 0.24], [256225, 0.32], [640600, 0.35], [Infinity, 0.37]],
  married: [[24800, 0.1], [100800, 0.12], [211400, 0.22], [403550, 0.24], [512450, 0.32], [768700, 0.35], [Infinity, 0.37]],
};

function parseNumber(value) {
  const number = Number(value);
  return value === '' || !Number.isFinite(number) ? null : number;
}

export function parseOptionalBalance(value) {
  if (value === '' || value == null) return { amount: 0, provided: false };
  const normalized = String(value).trim().toLowerCase();
  if (normalized === 'na' || normalized === 'n/a') return { amount: 0, provided: false };
  const number = Number(value);
  if (!Number.isFinite(number)) return { amount: 0, provided: false };
  return { amount: Math.max(0, number), provided: true };
}

function formatUsd(value) {
  return value == null || !Number.isFinite(value) ? '--' : new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(value);
}

// Compact currency for narrow axis ticks (e.g., $6.7M, $1.7M) to avoid overlap with the axis title.
function formatUsdCompact(value) {
  if (value == null || !Number.isFinite(value)) return '--';
  const abs = Math.abs(value);
  if (abs >= 1000000) return `$${(value / 1000000).toFixed(abs >= 10000000 ? 0 : 1)}M`;
  if (abs >= 1000) return `$${Math.round(value / 1000)}K`;
  return `$${Math.round(value)}`;
}

function formatPct(value) {
  return value == null || !Number.isFinite(value) ? '--' : `${(value * 100).toFixed(1)}%`;
}

function computeIncomeTargetPlan(householdIncome, annualSS, balanceAtRetirement) {
  const incomeTarget = Math.max(0, (householdIncome || 0) * INCOME_REPLACEMENT_RATE);
  const portfolioNeeded = Math.max(0, incomeTarget - Math.max(0, annualSS || 0));
  const safeBalance = Math.max(0, balanceAtRetirement || 0);
  const neededRate = safeBalance > 0 ? portfolioNeeded / safeBalance : Infinity;
  const meetsIncomeTarget = neededRate <= WITHDRAWAL_RATE;
  // Always draw the ACTUAL income need — never cap at 4% of balance. Capping let the balance
  // compound upward (real return > 4%) even when the need was deeply unsustainable, which
  // hid genuine under-funding behind a falsely rising chart.
  return {
    incomeTarget,
    portfolioNeeded,
    neededRate,
    meetsIncomeTarget,
    withdrawalRate: neededRate,
    annualWithdrawal: portfolioNeeded,
  };
}

function makeFixedWithdrawalPlan(householdIncome, annualSS, balanceForRate, annualWithdrawal) {
  const base = computeIncomeTargetPlan(householdIncome, annualSS, balanceForRate);
  const safeWithdrawal = Math.max(0, annualWithdrawal || 0);
  return {
    ...base,
    annualWithdrawal: safeWithdrawal,
    withdrawalRate: balanceForRate > 0 ? safeWithdrawal / balanceForRate : 0,
    meetsIncomeTarget: safeWithdrawal >= base.portfolioNeeded,
  };
}

function findMaxSustainableAnnualWithdrawal(balanceAtRetirement, measureWithdrawal) {
  if (balanceAtRetirement <= 0) return 0;

  const endsNearZeroAt95 = (withdrawal) => {
    const { balanceAt95, depletedEarly } = measureWithdrawal(withdrawal);
    return !depletedEarly && balanceAt95 <= SUSTAINABLE_BALANCE_TOLERANCE;
  };
  const survivesTo95 = (withdrawal) => !measureWithdrawal(withdrawal).depletedEarly;

  let lo = 0;
  let hi = Math.max(1000, balanceAtRetirement * WITHDRAWAL_RATE);
  let expansions = 0;
  while (!endsNearZeroAt95(hi) && survivesTo95(hi) && expansions < 12) {
    hi *= 1.5;
    expansions += 1;
  }

  if (!survivesTo95(hi)) {
    while (hi - lo > 50) {
      const mid = (lo + hi) / 2;
      if (survivesTo95(mid)) lo = mid;
      else hi = mid;
    }
    return Math.round(lo);
  }

  if (!endsNearZeroAt95(hi)) {
    return Math.round(hi);
  }

  while (hi - lo > 50) {
    const mid = (lo + hi) / 2;
    if (endsNearZeroAt95(mid)) lo = mid;
    else hi = mid;
  }
  return Math.round(lo);
}

function winningStrategyLabel(rothValue, traditionalValue) {
  if (traditionalValue - rothValue > 5000) return 'Traditional + invested tax savings';
  if (rothValue - traditionalValue > 5000) return 'Roth IRA / 401(k)';
  return 'Neither strategy clearly';
}

function shortStrategyName(label) {
  if (label.startsWith('Traditional')) return 'Traditional';
  if (label.startsWith('Roth')) return 'Roth';
  return label;
}

function getStandardDeduction(filingStatus) {
  return STANDARD_DEDUCTION[filingStatus === 'married' ? 'married' : 'single'];
}

function getBracketSchedule(filingStatus) {
  return BRACKET_SCHEDULE[filingStatus === 'married' ? 'married' : 'single'];
}

export function computeOrdinaryTax(taxableIncome, filingStatus) {
  const income = Math.max(0, taxableIncome || 0);
  const schedule = getBracketSchedule(filingStatus);
  let tax = 0;
  let previousLimit = 0;
  for (const [limit, rate] of schedule) {
    const bandWidth = Math.min(income, limit) - previousLimit;
    if (bandWidth <= 0) break;
    tax += bandWidth * rate;
    previousLimit = limit;
    if (income <= limit) break;
  }
  return tax;
}

export function computeMarginalRate(taxableIncome, filingStatus) {
  const income = Math.max(0, taxableIncome || 0);
  const schedule = getBracketSchedule(filingStatus);
  return schedule.find(([limit]) => income <= limit)?.[1] || 0.37;
}

export function taxableIncomeFromGross(grossIncome, filingStatus) {
  return Math.max(0, (grossIncome || 0) - getStandardDeduction(filingStatus));
}

// Marginal federal rate on the next dollar of ordinary income after the standard deduction (2026 brackets).
export function estimateCurrentTaxRate(filingStatus, grossIncome) {
  return computeMarginalRate(taxableIncomeFromGross(grossIncome, filingStatus), filingStatus);
}

// Traditional tax savings = deductible contribution × marginal rate on combined household income.
export function incrementalTaxSavingsFromDeduction(grossIncome, deductionAmount, filingStatus) {
  const marginalRate = estimateCurrentTaxRate(filingStatus, grossIncome);
  return Math.max(0, Math.max(0, deductionAmount || 0) * marginalRate);
}

export function rothFundingTaxCost(contribution, grossIncome, filingStatus) {
  const marginalRate = estimateCurrentTaxRate(filingStatus, grossIncome);
  if (marginalRate >= 0.99) return Math.max(0, contribution || 0);
  return Math.max(0, (contribution || 0) * marginalRate / (1 - marginalRate));
}

export function estimateAnnualSocialSecurity(householdIncome, filingStatus) {
  const income = Math.max(0, householdIncome || 0);
  const replacementRate = filingStatus === 'married' ? 0.32 : 0.30;
  const householdCap = filingStatus === 'married' ? 52000 : 28000;
  return Math.min(income * replacementRate, householdCap);
}

export function calculateRealReturn(nominalReturn, inflation) {
  return (1 + nominalReturn) / (1 + inflation) - 1;
}

function getPhaseRealReturns() {
  return {
    accumulationRealReturn: calculateRealReturn(NOMINAL_RETURN_ACCUMULATION, INFLATION),
    withdrawalRealReturn: calculateRealReturn(NOMINAL_RETURN_WITHDRAWAL, INFLATION),
  };
}

export function calculateFutureValue(balance, years, realReturn) {
  return Math.max(0, balance || 0) * (1 + realReturn) ** Math.max(0, years || 0);
}

function getRmdDivisor(age) {
  if (age < RMD_START_AGE) return null;
  if (RMD_DIVISORS[age]) return RMD_DIVISORS[age];
  return Math.max(10, RMD_DIVISORS[90] - (age - 90) * 0.7);
}

function computeRetirementYearTax({
  filingStatus,
  socialSecurityGross,
  traditionalWithdrawal,
  taxableWithdrawal,
}) {
  const ssTaxable = Math.max(0, socialSecurityGross || 0) * SS_TAXABLE_PORTION;
  const taxableAccountOrdinary = Math.max(0, taxableWithdrawal || 0) * 0.15;
  const taxableIncome = Math.max(
    0,
    Math.max(0, traditionalWithdrawal || 0) + ssTaxable + taxableAccountOrdinary - getStandardDeduction(filingStatus),
  );
  const federalTax = computeOrdinaryTax(taxableIncome, filingStatus);
  return {
    federalTax,
    marginalRate: computeMarginalRate(taxableIncome, filingStatus),
    taxableIncome,
  };
}

function afterTaxPortfolioValue({ iraBalance, taxableBalance, filingStatus, socialSecurityGross, withdrawalRate }) {
  const tradWithdrawal = Math.max(0, iraBalance || 0) * withdrawalRate;
  const taxableWithdrawal = Math.max(0, taxableBalance || 0) * withdrawalRate;
  const { federalTax } = computeRetirementYearTax({
    filingStatus,
    socialSecurityGross,
    traditionalWithdrawal: tradWithdrawal,
    taxableWithdrawal,
  });
  const liquidationTaxRate = iraBalance > 0 ? Math.min(0.95, federalTax / Math.max(tradWithdrawal, 1)) : 0;
  const taxableHaircut = 0.05;
  return Math.max(0, (iraBalance || 0) * (1 - liquidationTaxRate) + (taxableBalance || 0) * (1 - taxableHaircut));
}

function simulateRothPath({
  currentAge,
  retirementAge,
  endAge,
  annualContribution,
  filingStatus,
  householdIncome,
  accumulationRealReturn,
  withdrawalRealReturn,
  realReturn,
  startingRoth,
  withdrawalPlanFactory = null,
}) {
  const { accumulationRealReturn: defaultAccum, withdrawalRealReturn: defaultWithdraw } = getPhaseRealReturns();
  const accumReturn = accumulationRealReturn ?? realReturn ?? defaultAccum;
  const withdrawReturn = withdrawalRealReturn ?? realReturn ?? defaultWithdraw;
  const annualSS = estimateAnnualSocialSecurity(householdIncome, filingStatus);
  const ssStartAge = Math.max(SS_CLAIM_AGE, retirementAge);
  let roth = startingRoth;
  let withdrawalPlan = null;
  let depletedAge = null;
  const series = [];

  for (let age = currentAge; age <= endAge; age += 1) {
    const isAccumulating = age < retirementAge;
    const annualRothTaxCost = isAccumulating
      ? rothFundingTaxCost(annualContribution, householdIncome, filingStatus)
      : 0;

    if (isAccumulating) {
      roth += annualContribution;
    }

    const phaseReturn = age >= retirementAge ? withdrawReturn : accumReturn;
    roth *= 1 + phaseReturn;

    if (age === retirementAge) {
      withdrawalPlan = withdrawalPlanFactory
        ? withdrawalPlanFactory({
          rothBalance: roth,
          purchasingPowerAtRetirement: roth,
          annualSS,
          householdIncome,
          filingStatus,
        })
        : computeIncomeTargetPlan(householdIncome, annualSS, roth);
    }

    let portfolioWithdrawal = 0;
    if (age > retirementAge && withdrawalPlan && roth > 0) {
      portfolioWithdrawal = Math.min(roth, withdrawalPlan.annualWithdrawal);
      roth = Math.max(0, roth - portfolioWithdrawal);
      if (roth <= 0 && depletedAge == null) depletedAge = age;
    }

    const socialSecurity = age >= ssStartAge ? annualSS : 0;
    const { federalTax, marginalRate } = computeRetirementYearTax({
      filingStatus,
      socialSecurityGross: socialSecurity,
      traditionalWithdrawal: 0,
      taxableWithdrawal: 0,
    });
    const netAnnualIncome = age > retirementAge
      ? portfolioWithdrawal + socialSecurity - federalTax
      : 0;

    series.push({
      age,
      iraBalance: roth,
      taxableBalance: 0,
      purchasingPower: roth,
      netAnnualIncome,
      socialSecurity,
      marginalRate: age >= retirementAge ? marginalRate : estimateCurrentTaxRate(filingStatus, householdIncome),
      annualRothTaxCost,
      annualTaxSavings: 0,
      rmdAmount: 0,
    });
  }

  return { series, withdrawalPlan, depletedAge };
}

function simulateTraditionalPath({
  currentAge,
  retirementAge,
  endAge,
  annualContribution,
  filingStatus,
  householdIncome,
  accumulationRealReturn,
  withdrawalRealReturn,
  realReturn,
  startingTraditional,
  startingTaxable,
  withdrawalPlanFactory = null,
}) {
  const { accumulationRealReturn: defaultAccum, withdrawalRealReturn: defaultWithdraw } = getPhaseRealReturns();
  const accumReturn = accumulationRealReturn ?? realReturn ?? defaultAccum;
  const withdrawReturn = withdrawalRealReturn ?? realReturn ?? defaultWithdraw;
  const annualSS = estimateAnnualSocialSecurity(householdIncome, filingStatus);
  const ssStartAge = Math.max(SS_CLAIM_AGE, retirementAge);
  let traditional = startingTraditional;
  let taxable = startingTaxable;
  let withdrawalPlan = null;
  let depletedAge = null;
  const series = [];

  for (let age = currentAge; age <= endAge; age += 1) {
    const isAccumulating = age < retirementAge;
    const annualTaxSavings = isAccumulating
      ? incrementalTaxSavingsFromDeduction(householdIncome, annualContribution, filingStatus)
      : 0;

    if (isAccumulating) {
      traditional += annualContribution;
      taxable += annualTaxSavings;
    }

    const phaseReturn = age >= retirementAge ? withdrawReturn : accumReturn;
    traditional *= 1 + phaseReturn;
    taxable *= 1 + phaseReturn;

    let traditionalWithdrawal = 0;
    let taxableWithdrawal = 0;
    let rmdAmount = 0;

    if (age === retirementAge) {
      const purchasingPowerAtRetirement = afterTaxPortfolioValue({
        iraBalance: traditional,
        taxableBalance: taxable,
        filingStatus,
        socialSecurityGross: annualSS,
        withdrawalRate: WITHDRAWAL_RATE,
      });
      withdrawalPlan = withdrawalPlanFactory
        ? withdrawalPlanFactory({
          rothBalance: 0,
          purchasingPowerAtRetirement,
          traditionalBalance: traditional,
          taxableBalance: taxable,
          annualSS,
          householdIncome,
          filingStatus,
        })
        : computeIncomeTargetPlan(householdIncome, annualSS, purchasingPowerAtRetirement);
    }

    if (age > retirementAge && withdrawalPlan) {
      let remainingAfterTax = withdrawalPlan.annualWithdrawal;
      taxableWithdrawal = Math.min(taxable, remainingAfterTax);
      remainingAfterTax = Math.max(0, remainingAfterTax - taxableWithdrawal);
      const { marginalRate: bookRate } = computeRetirementYearTax({
        filingStatus,
        socialSecurityGross: age >= ssStartAge ? annualSS : 0,
        traditionalWithdrawal: 0,
        taxableWithdrawal,
      });
      const grossTradNeeded = bookRate >= 0.99
        ? remainingAfterTax
        : remainingAfterTax / Math.max(0.05, 1 - bookRate);
      traditionalWithdrawal = Math.min(traditional, grossTradNeeded);

      // RMDs force extra taxable withdrawals from the pre-tax balance after 75,
      // which is the primary tax drag pulling Traditional down vs Roth long-term.
      if (age >= RMD_START_AGE && traditional > 0) {
        rmdAmount = traditional / getRmdDivisor(age);
        traditionalWithdrawal = Math.max(traditionalWithdrawal, rmdAmount);
      }

      traditional = Math.max(0, traditional - traditionalWithdrawal);
      taxable = Math.max(0, taxable - taxableWithdrawal);

      const afterTaxRemaining = afterTaxPortfolioValue({
        iraBalance: traditional,
        taxableBalance: taxable,
        filingStatus,
        socialSecurityGross: age >= ssStartAge ? annualSS : 0,
        withdrawalRate: WITHDRAWAL_RATE,
      });
      if (afterTaxRemaining <= 0 && depletedAge == null) depletedAge = age;
    }

    const socialSecurity = age >= ssStartAge ? annualSS : 0;
    const { federalTax, marginalRate } = computeRetirementYearTax({
      filingStatus,
      socialSecurityGross: socialSecurity,
      traditionalWithdrawal: age > retirementAge ? traditionalWithdrawal : 0,
      taxableWithdrawal: age > retirementAge ? taxableWithdrawal : 0,
    });
    const grossAnnualIncome = age > retirementAge
      ? traditionalWithdrawal + taxableWithdrawal + socialSecurity
      : 0;
    const netAnnualIncome = Math.max(0, grossAnnualIncome - federalTax);
    const purchasingPower = afterTaxPortfolioValue({
      iraBalance: traditional,
      taxableBalance: taxable,
      filingStatus,
      socialSecurityGross: socialSecurity,
      withdrawalRate: WITHDRAWAL_RATE,
    });

    series.push({
      age,
      iraBalance: traditional,
      taxableBalance: taxable,
      purchasingPower,
      netAnnualIncome,
      socialSecurity,
      marginalRate,
      annualRothTaxCost: 0,
      annualTaxSavings,
      rmdAmount,
    });
  }

  return { series, withdrawalPlan, depletedAge };
}

export function buildLifecycleSeries(args) {
  const {
    rothWithdrawalPlanFactory = null,
    traditionalWithdrawalPlanFactory = null,
    ...pathArgs
  } = args;
  const rothResult = simulateRothPath({
    ...pathArgs,
    withdrawalPlanFactory: rothWithdrawalPlanFactory,
  });
  const traditionalResult = simulateTraditionalPath({
    ...pathArgs,
    withdrawalPlanFactory: traditionalWithdrawalPlanFactory,
  });
  return {
    series: rothResult.series.map((rothRow, index) => {
      const tradRow = traditionalResult.series[index];
      return {
        age: rothRow.age,
        rothPurchasingPower: rothRow.purchasingPower,
        traditionalPurchasingPower: tradRow.purchasingPower,
        rothNetIncome: rothRow.netAnnualIncome,
        traditionalNetIncome: tradRow.netAnnualIncome,
        socialSecurity: rothRow.socialSecurity,
        rothMarginalRate: rothRow.marginalRate,
        traditionalMarginalRate: tradRow.marginalRate,
        annualRothTaxCost: rothRow.annualRothTaxCost,
        annualTaxSavings: tradRow.annualTaxSavings,
        rmdAmount: tradRow.rmdAmount,
        rothBalance: rothRow.iraBalance,
        traditionalBalance: tradRow.iraBalance,
        taxSavingsTaxable: tradRow.taxableBalance,
      };
    }),
    rothWithdrawalPlan: rothResult.withdrawalPlan,
    traditionalWithdrawalPlan: traditionalResult.withdrawalPlan,
    rothDepletedAge: rothResult.depletedAge,
    traditionalDepletedAge: traditionalResult.depletedAge,
  };
}

export function buildProjectionSeries(args) {
  return buildLifecycleSeries({
    ...args,
    endAge: args.retirementAge,
    startingRoth: 0,
    startingTraditional: 0,
    startingTaxable: 0,
  }).series;
}

// Run the full lifecycle for a candidate contribution and report whether each
// path funds the full 80% income target at retirement.
//
// Do not target "balance near $0 at 95" here. Because withdrawals are capped at
// the 4% benchmark, an underfunded portfolio can withdraw very little and still
// drift toward $0, which created the implausibly tiny "minimum" result. The
// useful monotonic threshold is whether the full income target is funded.
function simulateFundingAtContribution(input, annualContribution) {
  const { accumulationRealReturn, withdrawalRealReturn } = getPhaseRealReturns();
  const endAge = Math.max(input.retirementAge, CHART_END_AGE);
  const lifecycle = buildLifecycleSeries({
    currentAge: input.currentAge,
    retirementAge: input.retirementAge,
    endAge,
    annualContribution,
    filingStatus: input.filingStatus,
    householdIncome: input.householdIncome,
    accumulationRealReturn,
    withdrawalRealReturn,
    startingRoth: input.rothBalance,
    startingTraditional: input.preTaxBalance,
    startingTaxable: input.taxableBalance,
  });
  const atAge95 = lifecycle.series.find((row) => row.age === CHART_END_AGE) || lifecycle.series[lifecycle.series.length - 1];
  return {
    rothMeetsTarget: lifecycle.rothWithdrawalPlan?.meetsIncomeTarget ?? false,
    traditionalMeetsTarget: lifecycle.traditionalWithdrawalPlan?.meetsIncomeTarget ?? false,
    rothAt95: atAge95.rothPurchasingPower,
    traditionalAt95: atAge95.traditionalPurchasingPower,
    rothDepletedAge: lifecycle.rothDepletedAge,
    traditionalDepletedAge: lifecycle.traditionalDepletedAge,
  };
}

// Smallest annual contribution at which BOTH paths sustainably fund the full 80% income target.
// Returns 0 if existing balances alone already fund it, or null if even 2x the current
// contribution can't fund it (i.e. the household is not over-saving). meetsIncomeTarget is
// monotonic in the contribution, so a binary search is valid.
function findMinContributionToFundTarget(input) {
  const fundsTarget = (contribution) => {
    const r = simulateFundingAtContribution(input, contribution);
    return r.rothMeetsTarget && r.traditionalMeetsTarget;
  };
  const current = Math.max(0, input.totalContribution || 0);

  if (fundsTarget(0)) return 0;

  let hi = Math.max(current * 2, 1000);
  let expansions = 0;
  while (!fundsTarget(hi) && expansions < 8) {
    hi *= 2;
    expansions += 1;
  }
  if (!fundsTarget(hi)) return null;

  let lo = 0;
  while (hi - lo > 25) {
    const mid = (lo + hi) / 2;
    if (fundsTarget(mid)) hi = mid;
    else lo = mid;
  }
  return Math.round(hi);
}

function measureRothWithdrawalOutcome(args, annualWithdrawal) {
  const endAge = Math.max(args.retirementAge, CHART_END_AGE);
  const annualSS = estimateAnnualSocialSecurity(args.householdIncome, args.filingStatus);
  const result = simulateRothPath({
    ...args,
    endAge,
    withdrawalPlanFactory: ({ rothBalance }) => makeFixedWithdrawalPlan(
      args.householdIncome,
      annualSS,
      rothBalance,
      annualWithdrawal,
    ),
  });
  const atAge95 = result.series.find((row) => row.age === CHART_END_AGE) || result.series[result.series.length - 1];
  return {
    balanceAt95: atAge95.iraBalance,
    depletedEarly: result.depletedAge != null && result.depletedAge < CHART_END_AGE,
  };
}

function measureTraditionalWithdrawalOutcome(args, annualWithdrawal) {
  const endAge = Math.max(args.retirementAge, CHART_END_AGE);
  const annualSS = estimateAnnualSocialSecurity(args.householdIncome, args.filingStatus);
  const result = simulateTraditionalPath({
    ...args,
    endAge,
    withdrawalPlanFactory: ({ purchasingPowerAtRetirement }) => makeFixedWithdrawalPlan(
      args.householdIncome,
      annualSS,
      purchasingPowerAtRetirement,
      annualWithdrawal,
    ),
  });
  const atAge95 = result.series.find((row) => row.age === CHART_END_AGE) || result.series[result.series.length - 1];
  return {
    balanceAt95: atAge95.purchasingPower,
    depletedEarly: result.depletedAge != null && result.depletedAge < CHART_END_AGE,
  };
}

function getRothBalanceAtRetirement(args) {
  const result = simulateRothPath({ ...args, endAge: args.retirementAge });
  const row = result.series.find((r) => r.age === args.retirementAge);
  return row?.iraBalance ?? 0;
}

function getTraditionalPurchasingPowerAtRetirement(args) {
  const annualSS = estimateAnnualSocialSecurity(args.householdIncome, args.filingStatus);
  const result = simulateTraditionalPath({ ...args, endAge: args.retirementAge });
  const row = result.series.find((r) => r.age === args.retirementAge);
  if (!row) return 0;
  return afterTaxPortfolioValue({
    iraBalance: row.iraBalance,
    taxableBalance: row.taxableBalance,
    filingStatus: args.filingStatus,
    socialSecurityGross: annualSS,
    withdrawalRate: WITHDRAWAL_RATE,
  });
}

function computeFundingAssessment(input) {
  const { accumulationRealReturn, withdrawalRealReturn } = getPhaseRealReturns();
  const args = {
    currentAge: input.currentAge,
    retirementAge: input.retirementAge,
    annualContribution: input.totalContribution,
    filingStatus: input.filingStatus,
    householdIncome: input.householdIncome,
    accumulationRealReturn,
    withdrawalRealReturn,
    startingRoth: input.rothBalance,
    startingTraditional: input.preTaxBalance,
    startingTaxable: input.taxableBalance,
  };
  const annualSS = estimateAnnualSocialSecurity(input.householdIncome, input.filingStatus);
  const incomeTarget = Math.max(0, (input.householdIncome || 0) * INCOME_REPLACEMENT_RATE);
  const portfolioNeeded = Math.max(0, incomeTarget - annualSS);

  const rothBalanceAtRetirement = getRothBalanceAtRetirement(args);
  const traditionalBalanceAtRetirement = getTraditionalPurchasingPowerAtRetirement(args);
  const rothSustainable = findMaxSustainableAnnualWithdrawal(
    rothBalanceAtRetirement,
    (withdrawal) => measureRothWithdrawalOutcome(args, withdrawal),
  );
  const traditionalSustainable = findMaxSustainableAnnualWithdrawal(
    traditionalBalanceAtRetirement,
    (withdrawal) => measureTraditionalWithdrawalOutcome(args, withdrawal),
  );
  const sustainableAnnualWithdrawal = Math.min(rothSustainable, traditionalSustainable);
  const fundingMode = sustainableAnnualWithdrawal >= portfolioNeeded ? 'over-funded' : 'under-funded';

  return {
    fundingMode,
    portfolioNeeded,
    incomeTarget,
    annualSS,
    rothSustainable,
    traditionalSustainable,
    sustainableAnnualWithdrawal,
    rothBalanceAtRetirement,
    traditionalBalanceAtRetirement,
  };
}

function computeOverSavingSignal(input, {
  rothAt95,
  traditionalAt95,
  rothDepletedAge,
  traditionalDepletedAge,
  accumulationRealReturn,
  yearsToRetirement,
  annualSocialSecurity,
}) {
  const hasShortfall = (rothDepletedAge != null && rothDepletedAge < CHART_END_AGE)
    || (traditionalDepletedAge != null && traditionalDepletedAge < CHART_END_AGE);
  if (hasShortfall) {
    return { show: false, hasShortfall: true };
  }

  const bothEndHealthy = rothAt95 > OVERSAVING_BALANCE_THRESHOLD && traditionalAt95 > OVERSAVING_BALANCE_THRESHOLD;
  if (!bothEndHealthy) {
    return { show: false, hasShortfall: false };
  }

  const currentContribution = Math.max(0, input.totalContribution || 0);
  const existingBalanceToday = Math.max(0, input.taxableBalance || 0)
    + Math.max(0, input.preTaxBalance || 0)
    + Math.max(0, input.rothBalance || 0);

  // Step 1–2: existing balances grown to retirement, and the income they alone can fund.
  const existingBalanceAtRetirement = existingBalanceToday * (1 + accumulationRealReturn) ** Math.max(0, yearsToRetirement);
  const existingBalanceWithdrawalCapacity = existingBalanceAtRetirement * WITHDRAWAL_RATE;

  // Step 3: income gap the portfolio must cover after Social Security.
  const incomeGap = Math.max(0, (input.householdIncome || 0) * INCOME_REPLACEMENT_RATE - Math.max(0, annualSocialSecurity || 0));

  // Case A: the household's total existing balances alone cover the income target. This uses
  // every bucket (taxable + pre-tax + Roth), so it stays valid even when any single bucket's
  // withdrawal rate is 4%-capped — check it before the income-target guard below.
  if (existingBalanceWithdrawalCapacity >= incomeGap) {
    return {
      show: true,
      hasShortfall: false,
      mode: 'existing-covers',
      existingBalanceToday,
      currentContribution,
    };
  }

  // Case B: smallest contribution that still funds the full 80% income target. Only
  // when current clearly exceeds this is the household genuinely over-saving (vs. the surplus that
  // the conservative 4% rule leaves behind for any funded plan).
  const minimumContribution = findMinContributionToFundTarget(input);
  if (minimumContribution != null) {
    // Sanity safeguard: a valid minimum must actually fund the target and be meaningfully below
    // the current contribution before we tell anyone they could contribute less.
    const safeguard = simulateFundingAtContribution(input, minimumContribution);
    const minimumActuallyFunds = safeguard.rothMeetsTarget && safeguard.traditionalMeetsTarget;
    const contributionHeadroom = Math.max(0, currentContribution - minimumContribution);
    const overSavingByMargin = currentContribution > 0
      && contributionHeadroom > currentContribution * OVERSAVING_HEADROOM_RATIO;
    if (minimumActuallyFunds && overSavingByMargin) {
      return {
        show: true,
        hasShortfall: false,
        mode: 'reduce-contributions',
        minimumContribution,
        currentContribution,
        contributionHeadroom,
        incomeGap,
        rothAt95,
        traditionalAt95,
      };
    }
  }

  // Case C: the household isn't over-contributing, but the conservative 4% withdrawal guardrail
  // still leaves a large balance unspent at 95 — more than the lump sum that would be needed to
  // fund the income target. That's a "spend more / retire earlier" signal, not "save less".
  const lumpToFundTarget = incomeGap > 0 ? incomeGap / WITHDRAWAL_RATE : 0;
  const largerEndingBalance = Math.max(rothAt95, traditionalAt95);
  if (largerEndingBalance > OVERSAVING_BALANCE_THRESHOLD && largerEndingBalance >= lumpToFundTarget) {
    return {
      show: true,
      hasShortfall: false,
      mode: 'projected-surplus',
      rothAt95,
      traditionalAt95,
    };
  }

  return { show: false, hasShortfall: false };
}

function buildProjection(input) {
  if ([input.currentAge, input.retirementAge, input.householdIncome, input.totalContribution].some((value) => value == null) || input.currentAge < 18 || input.retirementAge <= input.currentAge) return null;

  const years = input.retirementAge - input.currentAge;
  const { accumulationRealReturn, withdrawalRealReturn } = getPhaseRealReturns();
  const currentTaxRate = estimateCurrentTaxRate(input.filingStatus, input.householdIncome);
  const endAge = Math.max(input.retirementAge, CHART_END_AGE);
  const fundingAssessment = computeFundingAssessment(input);

  // The chart always reflects the ACTUAL income need being withdrawn (computeIncomeTargetPlan),
  // so an under-funded plan visibly declines/depletes instead of drifting upward.
  const lifecycle = buildLifecycleSeries({
    currentAge: input.currentAge,
    retirementAge: input.retirementAge,
    endAge,
    annualContribution: input.totalContribution,
    filingStatus: input.filingStatus,
    householdIncome: input.householdIncome,
    accumulationRealReturn,
    withdrawalRealReturn,
    startingRoth: input.rothBalance,
    startingTraditional: input.preTaxBalance,
    startingTaxable: input.taxableBalance,
  });

  const { series, rothWithdrawalPlan, traditionalWithdrawalPlan, rothDepletedAge, traditionalDepletedAge } = lifecycle;
  const atRetirement = series.find((row) => row.age === input.retirementAge) || series[series.length - 1];
  const atAge95 = series.find((row) => row.age === CHART_END_AGE) || series[series.length - 1];
  const annualTaxSavings = incrementalTaxSavingsFromDeduction(input.householdIncome, input.totalContribution, input.filingStatus);
  const annualRothTaxCost = rothFundingTaxCost(input.totalContribution, input.householdIncome, input.filingStatus);
  const annualSocialSecurity = estimateAnnualSocialSecurity(input.householdIncome, input.filingStatus);
  const stdDed = getStandardDeduction(input.filingStatus);
  const rothPurchasingPower = atRetirement.rothPurchasingPower;
  const traditionalPurchasingPower = atRetirement.traditionalPurchasingPower;
  const retirementMarginalRate = atRetirement.traditionalMarginalRate;
  const rothAt95 = atAge95.rothPurchasingPower;
  const traditionalAt95 = atAge95.traditionalPurchasingPower;
  const age95Gap = traditionalAt95 - rothAt95;
  const traditionalGrossRate = (traditionalWithdrawalPlan?.withdrawalRate ?? 0) * (1 + retirementMarginalRate);
  const retirementWinner = winningStrategyLabel(rothPurchasingPower, traditionalPurchasingPower);
  const longTermWinner = winningStrategyLabel(rothAt95, traditionalAt95);
  // True when the leader at retirement is overtaken by the other strategy by age 95.
  const crossover = retirementWinner !== longTermWinner
    && retirementWinner !== 'Neither strategy clearly'
    && longTermWinner !== 'Neither strategy clearly';
  const winningStrategy = retirementWinner;
  const leaderWithdrawalPlan = traditionalPurchasingPower >= rothPurchasingPower
    ? traditionalWithdrawalPlan
    : rothWithdrawalPlan;
  const retirementYears = CHART_END_AGE - input.retirementAge;
  const annualIncomeGapAt95 = retirementYears > 0 ? Math.abs(age95Gap) / retirementYears : 0;

  let overSaving;
  if (fundingAssessment.fundingMode === 'under-funded') {
    overSaving = {
      show: true,
      hasShortfall: false,
      mode: 'under-funded',
      sustainableAnnualWithdrawal: fundingAssessment.sustainableAnnualWithdrawal,
      incomeTarget: fundingAssessment.portfolioNeeded,
      incomeGap: Math.max(0, fundingAssessment.portfolioNeeded - fundingAssessment.sustainableAnnualWithdrawal),
    };
  } else {
    overSaving = computeOverSavingSignal(input, {
      rothAt95,
      traditionalAt95,
      rothDepletedAge,
      traditionalDepletedAge,
      accumulationRealReturn,
      yearsToRetirement: years,
      annualSocialSecurity,
    });
  }

  return {
    years,
    accumulationRealReturn,
    withdrawalRealReturn,
    currentTaxRate,
    retirementMarginalRate,
    standardDeduction: stdDed,
    annualTaxSavings,
    annualRothTaxCost,
    annualSocialSecurity,
    rothPurchasingPower,
    traditionalPurchasingPower,
    rothAt95,
    traditionalAt95,
    age95Gap,
    annualIncomeGapAt95,
    winningStrategy,
    retirementWinner,
    longTermWinner,
    crossover,
    rothWithdrawalPlan,
    traditionalWithdrawalPlan,
    traditionalGrossRate,
    leaderWithdrawalPlan,
    rothDepletedAge,
    traditionalDepletedAge,
    overSaving,
    fundingMode: fundingAssessment.fundingMode,
    sustainableAnnualWithdrawal: fundingAssessment.sustainableAnnualWithdrawal,
    portfolioNeeded: fundingAssessment.portfolioNeeded,
    valueAtRetirement: rothPurchasingPower,
    traditionalAfterTax: traditionalPurchasingPower,
    traditionalWithSavings: traditionalPurchasingPower,
    series,
    balanceMeta: input.balanceMeta,
    householdIncome: input.householdIncome,
    currentBalances: {
      taxable: input.balanceMeta.taxable.provided
        ? calculateFutureValue(input.taxableBalance, years, accumulationRealReturn)
        : null,
      preTax: input.balanceMeta.preTax.provided
        ? calculateFutureValue(input.preTaxBalance, years, accumulationRealReturn)
        : null,
      roth: input.balanceMeta.roth.provided
        ? calculateFutureValue(input.rothBalance, years, accumulationRealReturn)
        : null,
    },
  };
}

function buildVerdict({
  currentTaxRate,
  retirementMarginalRate,
  rothPurchasingPower,
  traditionalPurchasingPower,
  age95Gap,
  annualIncomeGapAt95,
  retirementWinner,
  longTermWinner,
  crossover,
  overSaving,
}) {
  const currentPct = Math.round(currentTaxRate * 100);
  const futurePct = Math.round(retirementMarginalRate * 100);
  const purchasingPowerGap = traditionalPurchasingPower - rothPurchasingPower;
  const absRetirementGap = Math.abs(purchasingPowerGap);
  const absAge95Gap = Math.abs(age95Gap);
  const perYearGap = formatUsd(annualIncomeGapAt95);
  const retirementShort = shortStrategyName(retirementWinner);
  const longTermShort = shortStrategyName(longTermWinner);

  let headline;
  if (purchasingPowerGap > 5000) {
    headline = `Traditional + invested tax savings appears to deliver about ${formatUsd(purchasingPowerGap)} more after-tax purchasing power at retirement.`;
  } else if (purchasingPowerGap < -5000) {
    headline = `Roth IRA / 401(k) appears to deliver about ${formatUsd(absRetirementGap)} more after-tax purchasing power at retirement.`;
  } else {
    headline = 'Roth and Traditional look close on after-tax purchasing power at retirement.';
  }

  let detail;
  if (crossover && purchasingPowerGap > 5000) {
    detail = `${retirementShort} leads at retirement by ${formatUsd(absRetirementGap)} in purchasing power — but RMDs and taxes on later withdrawals flip this over time. By age ${CHART_END_AGE}, ${longTermShort} leads by an estimated ${formatUsd(absAge95Gap)}, roughly ${perYearGap}/yr more in spendable income.`;
  } else if (crossover && purchasingPowerGap < -5000) {
    detail = `${retirementShort} leads at retirement by ${formatUsd(absRetirementGap)} in purchasing power and extends that advantage to age ${CHART_END_AGE}, leading by an estimated ${formatUsd(absAge95Gap)} — roughly ${perYearGap}/yr more in spendable income.`;
  } else if (Math.abs(purchasingPowerGap) > 5000) {
    detail = `${retirementShort} leads at retirement by ${formatUsd(absRetirementGap)} in purchasing power. By age ${CHART_END_AGE}, that lead grows to an estimated ${formatUsd(absAge95Gap)} — roughly ${perYearGap}/yr more in spendable income.`;
  } else {
    detail = `At today's ${currentPct}% marginal rate and an estimated ${futurePct}% rate once Social Security and withdrawals stack up, the strategies are roughly equivalent at retirement — other factors likely decide the winner.`;
  }

  if (overSaving?.mode === 'reduce-contributions') {
    detail += ' Both projections also show substantial balances remaining at 95, which raises a separate question: are you saving more than you need to — and spending less today than you could?';
  } else if (overSaving?.mode === 'existing-covers') {
    detail += ' Your existing balances alone may already track to your retirement target, which shifts the question from how much you save to where you save it.';
  } else if (overSaving?.mode === 'projected-surplus') {
    detail += ' This plan is also projected to leave a large balance unspent at 95 — often a sign there is room to spend more or retire earlier than a conservative 4% withdrawal assumes.';
  } else if (overSaving?.mode === 'under-funded') {
    detail += ' At your current contribution rate, this plan is projected to fall short of fully funding your income target by age 95 — a gap worth addressing before retirement, not after.';
  }

  return {
    headline,
    detail,
    age95Line: null,
  };
}

function OverSavingSignal({ overSaving }) {
  if (!overSaving?.show) return null;

  if (overSaving.mode === 'under-funded') {
    return (
      <div className="rtc-oversaving-signal" role="note">
        <p className="rtc-oversaving-title">What this projection shows</p>
        <p>
          At your current contribution rate, this portfolio is projected to sustainably support about{' '}
          <strong>{formatUsd(overSaving.sustainableAnnualWithdrawal)}/yr</strong> through age {CHART_END_AGE} — not the{' '}
          <strong>{formatUsd(overSaving.incomeTarget)}/yr</strong> needed to replace 80% of your household income.
        </p>
        <p>
          That&apos;s a gap of about <strong>{formatUsd(overSaving.incomeGap)}/yr</strong>. Closing it could mean increasing contributions, adjusting your retirement age, or revisiting the income target itself.
        </p>
        <p className="rtc-oversaving-caveat">This is a simplified illustration, not a withdrawal plan. Sequence-of-returns risk, healthcare costs, and changes in your circumstances can all change this number significantly.</p>
        <p className="rtc-oversaving-footnote">A comprehensive financial plan can map out what closing that gap looks like.</p>
      </div>
    );
  }

  if (overSaving.mode === 'projected-surplus') {
    return (
      <div className="rtc-oversaving-signal" role="note">
        <p className="rtc-oversaving-title">What this projection also shows</p>
        <p>
          Withdrawals here are modeled at a conservative 4% guardrail — and even so, this plan is projected to still hold about{' '}
          <strong>{formatUsd(overSaving.rothAt95)}</strong> (Roth) and <strong>{formatUsd(overSaving.traditionalAt95)}</strong> (Traditional) at age {CHART_END_AGE}.
        </p>
        <p>
          When a plan ends with this much unspent, it usually points one of two ways: you may be able to spend more — or retire earlier — than a 4% guardrail assumes, or you have room to redirect some savings toward life today. Which one fits is a planning question, not a math one.
        </p>
        <p className="rtc-oversaving-caveat">This is a simplified illustration, not a withdrawal plan. Sequence-of-returns risk, healthcare costs, and changes in your circumstances can all change this number significantly.</p>
        <p className="rtc-oversaving-footnote">A comprehensive financial plan can help you direct that surplus.</p>
      </div>
    );
  }

  if (overSaving.mode === 'existing-covers') {
    return (
      <div className="rtc-oversaving-signal" role="note">
        <p className="rtc-oversaving-title">What this projection also shows</p>
        <p>
          Based on your existing balances (~<strong>{formatUsd(overSaving.existingBalanceToday)}</strong> saved today), you may already be on track to cover your retirement income target — before counting future contributions.
        </p>
        <p>
          That doesn&apos;t mean you should stop saving. It means the question shifts from <em>how much</em> to <em>where</em>. Tax location — which dollars go into Roth vs. Traditional vs. taxable — may matter more than the contribution amount at this stage.
        </p>
        <p className="rtc-oversaving-caveat">This is a simplified illustration, not a withdrawal plan. Sequence-of-returns risk, healthcare costs, and changes in your circumstances can all change this number significantly.</p>
        <p className="rtc-oversaving-footnote">A comprehensive financial plan can help you answer that question.</p>
      </div>
    );
  }

  return (
    <div className="rtc-oversaving-signal" role="note">
      <p className="rtc-oversaving-title">What this projection also shows</p>
      <p>
        Both strategies are projected to leave large balances at {CHART_END_AGE} — an estimated{' '}
        <strong>{formatUsd(overSaving.rothAt95)}</strong> (Roth) and <strong>{formatUsd(overSaving.traditionalAt95)}</strong> (Traditional) — while still funding your 80% income target.
      </p>
      <p>
        To stay on track through age {CHART_END_AGE}, this projection suggests you may only need to contribute about{' '}
        <strong>{formatUsd(overSaving.minimumContribution)}/yr</strong> — vs. your current <strong>{formatUsd(overSaving.currentContribution)}/yr</strong>. That&apos;s a potential <strong>{formatUsd(overSaving.contributionHeadroom)}/yr</strong> you could redirect to experiences, debt payoff, or other goals now — while you&apos;re younger and can enjoy it.
      </p>
      <p className="rtc-oversaving-caveat">This is a simplified illustration, not a withdrawal plan. Sequence-of-returns risk, healthcare costs, and changes in your circumstances can all change this number significantly.</p>
      <p className="rtc-oversaving-footnote">The right answer depends on your full picture.</p>
    </div>
  );
}

function ProjectionChart({
  series,
  retirementAge,
  portfolioNeeded,
  rothRate,
  traditionalGrossRate,
  rothAt95,
  traditionalAt95,
  age95Gap,
  fundingMode,
  sustainableAnnualWithdrawal,
}) {
  const width = 820;
  const height = 360;
  const pad = { left: 76, right: 36, top: 44, bottom: 48 };
  const plotWidth = width - pad.left - pad.right;
  const plotHeight = height - pad.top - pad.bottom;
  const startAge = series[0].age;
  const endAge = series[series.length - 1].age;

  const wealthMaximum = Math.max(
    1,
    ...series.flatMap((row) => [row.rothPurchasingPower, row.traditionalPurchasingPower]),
  );
  const yMax = Math.ceil(wealthMaximum / 50000) * 50000;

  const x = (age) => pad.left + ((age - startAge) / Math.max(1, endAge - startAge)) * plotWidth;
  const y = (value) => pad.top + plotHeight - (value / yMax) * plotHeight;
  const path = (key) => series.map((row, index) => `${index === 0 ? 'M' : 'L'} ${x(row.age).toFixed(1)} ${y(row[key]).toFixed(1)}`).join(' ');

  const labels = [startAge, retirementAge, endAge]
    .filter((age, index, arr) => age >= startAge && age <= endAge && arr.indexOf(age) === index);
  const retirementX = x(retirementAge);
  const withdrawalYears = endAge - retirementAge;
  const isUnderFunded = fundingMode === 'under-funded';

  return (
    <div className="rtc-chart-wrap">
      <h3>Projected After-Tax Wealth Over Time</h3>
      <p className="rtc-chart-assumptions">
        Today&apos;s dollars · {formatPct(NOMINAL_RETURN_ACCUMULATION)} return while saving, {formatPct(NOMINAL_RETURN_WITHDRAWAL)} return in retirement · {formatPct(INFLATION)} inflation
      </p>
      <p>
        {isUnderFunded ? (
          <>
            Accumulation to {retirementAge}, then {withdrawalYears}-year withdrawal at the portfolio&apos;s sustainable rate — below your income-replacement target.
          </>
        ) : (
          <>
            Accumulation to {retirementAge}, then {withdrawalYears}-year withdrawal at income-replacement rate. Traditional withdrawals taxed at est. retirement rate; Roth withdrawals tax-free.
          </>
        )}
      </p>
      <svg viewBox={`0 0 ${width} ${height}`} role="img" aria-label="Projected after-tax wealth over time for Roth and Traditional strategies">
        {[0, 0.25, 0.5, 0.75, 1].map((tick) => (
          <g key={`wealth-${tick}`}>
            <line className="rtc-grid" x1={pad.left} x2={width - pad.right} y1={y(yMax * tick)} y2={y(yMax * tick)} />
            <text className="rtc-axis" x={pad.left - 8} y={y(yMax * tick) + 4} textAnchor="end">{formatUsdCompact(yMax * tick)}</text>
          </g>
        ))}
        {labels.map((age) => <text key={age} className="rtc-axis" x={x(age)} y={height - 14} textAnchor="middle">{age}</text>)}
        <text className="rtc-axis" x={width / 2} y={height - 2} textAnchor="middle">Age</text>
        <text className="rtc-axis rtc-axis--ylabel" x={16} y={pad.top + plotHeight / 2} textAnchor="middle" transform={`rotate(-90 16 ${pad.top + plotHeight / 2})`}>After-tax wealth (today&apos;s $)</text>
        <line className="rtc-retirement-line" x1={retirementX} x2={retirementX} y1={pad.top} y2={pad.top + plotHeight} />
        <path d={path('rothPurchasingPower')} className="rtc-line rtc-line--roth" />
        <path d={path('traditionalPurchasingPower')} className="rtc-line rtc-line--savings" />
      </svg>
      <div className="rtc-legend">
        <span><i className="rtc-swatch rtc-swatch--roth" />Roth IRA/401(k) purchasing power</span>
        <span><i className="rtc-swatch rtc-swatch--savings" />Traditional + tax savings purchasing power</span>
      </div>
      <p className="rtc-chart-disclaimer">
        Illustrative only — {formatPct(NOMINAL_RETURN_ACCUMULATION)} return while saving, {formatPct(NOMINAL_RETURN_WITHDRAWAL)} in retirement, {formatPct(INFLATION)} inflation, and tax brackets held constant for {endAge - retirementAge}+ years.
      </p>
      <div className="rtc-age95-stats">
        <p className="rtc-age95-stats-title">At age {endAge} (est.):</p>
        <div className="rtc-age95-stats-grid">
          <div><span>Roth IRA/401(k) remaining balance</span><strong>{formatUsd(rothAt95)}</strong></div>
          <div><span>Traditional + tax savings remaining</span><strong>{formatUsd(traditionalAt95)}</strong></div>
          <div><span>Difference</span><strong>{formatUsd(Math.abs(age95Gap))}</strong></div>
          <div><span>Withdrawal needed</span><strong>~{formatUsd(portfolioNeeded)}/yr (80% income target − SS)</strong></div>
          {isUnderFunded ? (
            <div className="rtc-age95-stats-highlight">
              <span>Portfolio can sustain</span>
              <strong>~{formatUsd(sustainableAnnualWithdrawal)}/yr through age {endAge}</strong>
            </div>
          ) : (
            <>
              <div><span>Roth withdrawal rate at retirement</span><strong>{formatPct(rothRate)}</strong></div>
              <div><span>Traditional withdrawal rate</span><strong>{formatPct(traditionalGrossRate)} (gross, pre-tax)</strong></div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function RothTraditionalCalculator() {
  const { trackComplete, trackFieldInteraction, trackReset, trackResultView } = useCalculatorAnalytics({ calculatorId: 'roth_vs_traditional_calculator', calculatorName: 'roth_analysis' });
  const [values, setValues] = useState(DEFAULTS);
  const isMarried = values.filingStatus === 'married';
  const setField = (field) => (value) => setValues((current) => ({ ...current, [field]: value }));

  const spouseIncome = isMarried ? parseNumber(values.spouseIncome) : null;
  const baseHouseholdIncome = parseNumber(values.householdIncome);
  const effectiveHouseholdIncome = baseHouseholdIncome != null
    ? baseHouseholdIncome + (spouseIncome || 0)
    : null;
  const primaryAge = parseNumber(values.currentAge);
  const primaryRetirementAge = parseNumber(values.retirementAge);
  const spouseAge = parseNumber(values.spouseAge);
  const spouseRetirementAge = parseNumber(values.spouseRetirementAge);
  const spouseTimelineDiffers = isMarried && (
    (spouseAge != null && primaryAge != null && spouseAge !== primaryAge)
    || (spouseRetirementAge != null && primaryRetirementAge != null && spouseRetirementAge !== primaryRetirementAge)
  );

  const balanceMeta = useMemo(() => ({
    taxable: parseOptionalBalance(values.taxableBalance),
    preTax: parseOptionalBalance(values.preTaxBalance),
    roth: parseOptionalBalance(values.rothBalance),
  }), [values.preTaxBalance, values.rothBalance, values.taxableBalance]);

  const input = useMemo(() => ({
    filingStatus: values.filingStatus,
    currentAge: primaryAge,
    retirementAge: primaryRetirementAge,
    householdIncome: effectiveHouseholdIncome,
    totalContribution: (parseNumber(values.annualContribution) || 0) + (isMarried ? parseNumber(values.spouseContribution) || 0 : 0),
    taxableBalance: balanceMeta.taxable.amount,
    preTaxBalance: balanceMeta.preTax.amount,
    rothBalance: balanceMeta.roth.amount,
    balanceMeta,
  }), [balanceMeta, effectiveHouseholdIncome, isMarried, primaryAge, primaryRetirementAge, values]);

  const projection = useMemo(() => buildProjection(input), [input]);
  const verdict = projection ? buildVerdict(projection) : null;

  useEffect(() => {
    if (!projection) return;
    trackResultView({ result_category: 'traditional_savings_modeled' });
    trackComplete({ result_category: 'traditional_savings_modeled' });
  }, [projection, trackComplete, trackResultView]);

  const resetCalculator = () => { setValues(DEFAULTS); trackReset(); };
  const firstIncomeRow = projection?.series.find((r) => r.age === input.retirementAge + 1);
  const stdDedLabel = isMarried ? formatUsd(STANDARD_DEDUCTION.married) : formatUsd(STANDARD_DEDUCTION.single);

  return (
    <div className="pete-page">
      <header className="pete-header">
        <div className="pete-header-inner">
          <Link className="pete-logo-link" to="/links">
            <img src={BRAND.logoSrc} alt="Bautista Planning and Analytics" className="pete-logo-img" width={440} height={88} decoding="async" />
          </Link>
        </div>
      </header>
      <main className="pete-body">
        <div className="pete-inner">
          <div className="pete-title-block rtc-hero">
            <h1>The Wrong Choice Here Could Cost You $200,000+</h1>
            <p className="pete-tax-year rtc-hero-sub">See which strategy fits your situation in 60 seconds.</p>
            <p className="pete-social-calculator-note">
              Coming from TikTok, Reels, or Threads? Use this calculator, then compare your result to
              the decision you&apos;re actually trying to make.
            </p>
          </div>
          <div className="pete-disclaimer-banner" role="note">
            <strong>Broad educational example only.</strong> This compares after-tax purchasing power: Roth uses today&apos;s marginal federal brackets (after the {stdDedLabel} standard deduction); Traditional reinvests bracket-based tax savings in taxable. Social Security is estimated to show how retirement income may affect your marginal bracket. Stock options, pensions, employer plans, and state taxes are not modeled.
          </div>
          <div className="pete-card">
            <h2>Where Are You Today?</h2>
            <section className="rtc-section">
              <h3>Filing status</h3>
              <div className="rtc-toggle" role="group" aria-label="Filing status">
                <button
                  type="button"
                  className={`rtc-toggle-btn ${!isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={!isMarried}
                  onClick={() => { trackFieldInteraction('filing_status'); setField('filingStatus')('single'); }}
                >
                  Single
                </button>
                <button
                  type="button"
                  className={`rtc-toggle-btn ${isMarried ? 'rtc-toggle-btn--active' : ''}`}
                  aria-pressed={isMarried}
                  onClick={() => { trackFieldInteraction('filing_status'); setField('filingStatus')('married'); }}
                >
                  Married
                </button>
              </div>
            </section>
            <section className="rtc-section">
              <h3>Your details</h3>
              {isMarried ? (
                <div className="rtc-couple-grid">
                  <Field id="rtc-current-age" fieldName="current_age" label="Your age" value={values.currentAge} setValue={setField('currentAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-retirement-age" fieldName="retirement_age" label="Your retirement age" value={values.retirementAge} setValue={setField('retirementAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-income" fieldName="annual_household_income" label="Your annual income" value={values.householdIncome} setValue={setField('householdIncome')} step="1000" onInteract={trackFieldInteraction} />
                  <Field id="rtc-contribution" fieldName="ira_contribution" label="Your IRA / 401(k) contribution" value={values.annualContribution} setValue={setField('annualContribution')} step="100" onInteract={trackFieldInteraction} />
                  <Field id="rtc-spouse-age" fieldName="spouse_current_age" label="Spouse age" value={values.spouseAge} setValue={setField('spouseAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-spouse-retirement-age" fieldName="spouse_retirement_age" label="Spouse retirement age" value={values.spouseRetirementAge} setValue={setField('spouseRetirementAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-spouse-income" fieldName="spouse_annual_income" label="Spouse annual income" value={values.spouseIncome} setValue={setField('spouseIncome')} step="1000" onInteract={trackFieldInteraction} />
                  <Field id="rtc-spouse-contribution" fieldName="spouse_ira_contribution" label="Spouse IRA / 401(k) contribution" value={values.spouseContribution} setValue={setField('spouseContribution')} step="100" onInteract={trackFieldInteraction} />
                </div>
              ) : (
                <div className="rtc-input-grid">
                  <Field id="rtc-current-age" fieldName="current_age" label="Current age" value={values.currentAge} setValue={setField('currentAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-retirement-age" fieldName="retirement_age" label="Retirement age" value={values.retirementAge} setValue={setField('retirementAge')} onInteract={trackFieldInteraction} />
                  <Field id="rtc-income" fieldName="annual_household_income" label="Annual income" value={values.householdIncome} setValue={setField('householdIncome')} step="1000" onInteract={trackFieldInteraction} />
                  <Field id="rtc-contribution" fieldName="ira_contribution" label="Annual IRA / 401(k) contribution" value={values.annualContribution} setValue={setField('annualContribution')} step="100" onInteract={trackFieldInteraction} />
                </div>
              )}
              <div className="rtc-input-grid rtc-calculated-grid">
                <CalculatedField label="Years until retirement" value={projection ? `${projection.years} years` : '--'} />
                {projection && (
                  <>
                    <CalculatedField
                      label="Est. federal tax to fund Roth (this year)"
                      value={formatUsd(projection.annualRothTaxCost)}
                      hint="Marginal bracket applied to gross-up the after-tax contribution"
                    />
                    <CalculatedField
                      label="Est. tax savings reinvested (Traditional)"
                      value={formatUsd(projection.annualTaxSavings)}
                      hint={`Bracket-based savings after ${stdDedLabel} standard deduction`}
                    />
                  </>
                )}
              </div>
              {spouseTimelineDiffers && <p className="rtc-spouse-timeline-note">Calculations use the primary filer&apos;s timeline. A full plan accounts for both timelines.</p>}
            </section>
            <section className="rtc-section">
              <h3>Current balances (optional)</h3>
              <p className="rtc-balance-hint">Enter <strong>N/A</strong> if you don&apos;t know a balance — annual contributions are still modeled.</p>
              <div className="rtc-input-grid">
                <BalanceField id="rtc-taxable-balance" fieldName="taxable_brokerage_balance" label="Taxable brokerage balance" value={values.taxableBalance} setValue={setField('taxableBalance')} onInteract={trackFieldInteraction} />
                <BalanceField id="rtc-pretax-balance" fieldName="pre_tax_retirement_balance" label="Pre-tax IRA / 401(k) balance" value={values.preTaxBalance} setValue={setField('preTaxBalance')} onInteract={trackFieldInteraction} />
                <BalanceField id="rtc-roth-balance" fieldName="roth_retirement_balance" label="Roth IRA / 401(k) balance" value={values.rothBalance} setValue={setField('rothBalance')} onInteract={trackFieldInteraction} />
              </div>
            </section>
            <button className="pete-secondary-action" type="button" onClick={resetCalculator}>Reset calculator</button>
          </div>
          {projection && (
            <>
              <div className="pete-card">
                <ProjectionChart
                  series={projection.series}
                  retirementAge={input.retirementAge}
                  portfolioNeeded={projection.portfolioNeeded ?? projection.rothWithdrawalPlan?.portfolioNeeded ?? 0}
                  rothRate={projection.rothWithdrawalPlan?.withdrawalRate ?? 0}
                  traditionalGrossRate={projection.traditionalGrossRate ?? 0}
                  rothAt95={projection.rothAt95}
                  traditionalAt95={projection.traditionalAt95}
                  age95Gap={projection.age95Gap}
                  fundingMode={projection.fundingMode}
                  sustainableAnnualWithdrawal={projection.sustainableAnnualWithdrawal}
                />
                <OverSavingSignal overSaving={projection.overSaving} />
              </div>
              <div className="pete-card">
                <h2>Purchasing power at retirement</h2>
                <div className="rtc-results-table">
                  <div className="rtc-results-row rtc-results-row--header rtc-results-row--compact">
                    <div>Strategy</div>
                    <div>After-tax purchasing power</div>
                    <div>Est. net annual income</div>
                    <div>Main idea</div>
                  </div>
                  <ResultsRow
                    name="Roth IRA / 401(k)"
                    value={projection.rothPurchasingPower}
                    afterTax={firstIncomeRow?.rothNetIncome ?? 0}
                    idea="Tax-free withdrawals compound over a long retirement — Roth leads by age 95 despite lower purchasing power at 67."
                  />
                  <ResultsRow
                    name="Traditional + invested tax savings"
                    value={projection.traditionalPurchasingPower}
                    afterTax={firstIncomeRow?.traditionalNetIncome ?? 0}
                    idea="Saves more today and leads at retirement, but taxed withdrawals and RMDs erode the advantage over time."
                  />
                </div>
                {verdict && (
                  <div className="rtc-verdict-card" role="status">
                    <p className="rtc-verdict-label">📌 Based on your inputs:</p>
                    <p className="rtc-verdict-headline">{verdict.headline}</p>
                    <p className="rtc-verdict-detail">{verdict.detail}</p>
                  </div>
                )}
                <p className="rtc-assumption-note">
                  Today&apos;s marginal rate (after {stdDedLabel} deduction): {(projection.currentTaxRate * 100).toFixed(0)}% · Est. marginal rate at retirement (with SS): {(projection.retirementMarginalRate * 100).toFixed(0)}% · Est. Social Security: {formatUsd(projection.annualSocialSecurity)}/yr from age {Math.max(SS_CLAIM_AGE, input.retirementAge)}
                  {spouseIncome ? ` · Household income for tax estimate: ${formatUsd(effectiveHouseholdIncome)}` : ''}
                </p>
              </div>
              <div className="pete-card rtc-interpretation">
                <h2>Here&apos;s What This Means for You</h2>
                <p>
                  The table shows the gap at retirement. The chart shows what happens over the next {CHART_END_AGE - input.retirementAge} years. They tell different parts of the same story.
                </p>
                {projection.crossover ? (
                  <p>
                    <strong>{shortStrategyName(projection.retirementWinner)} leads at {input.retirementAge}. {shortStrategyName(projection.longTermWinner)} leads at {CHART_END_AGE}.</strong>{' '}
                    The crossover happens because Traditional withdrawals are taxed — and as RMDs kick in, that tax drag compounds year after year.
                  </p>
                ) : projection.longTermWinner !== 'Neither strategy clearly' ? (
                  <p>
                    <strong>{shortStrategyName(projection.longTermWinner)} leads at both {input.retirementAge} and {CHART_END_AGE}.</strong>{' '}
                    The gap reflects how each strategy handles taxes during accumulation and withdrawal.
                  </p>
                ) : (
                  <p>
                    The strategies land close at retirement and remain close through age {CHART_END_AGE} on this illustration.
                  </p>
                )}
                {projection.overSaving?.show && projection.overSaving.mode !== 'under-funded' && (
                  <p>
                    But there&apos;s a third question the numbers raise: both projections show millions remaining at age {CHART_END_AGE}. That&apos;s not just a retirement planning question — it&apos;s a question about how you want to live now. Money spent at 55 is worth more to most people than money left unspent at 85.
                  </p>
                )}
                <p className="rtc-verdict-sentence">
                  <strong>A comprehensive financial plan can help you find the right balance — not just between Roth and Traditional, but between saving for the future and living fully today.</strong>
                </p>
                <p className="pete-caveat rtc-disclaimer">
                  This is not a recommendation. This calculator uses fixed assumptions — {formatPct(NOMINAL_RETURN_ACCUMULATION)} return while saving, {formatPct(NOMINAL_RETURN_WITHDRAWAL)} return in retirement, {formatPct(INFLATION)} inflation, and today&apos;s tax brackets projected unchanged for {CHART_END_AGE - input.retirementAge}+ years — to illustrate a concept, not predict an outcome. Real returns vary year to year, tax law changes, and your actual mix of income sources (Social Security timing, pensions, RSUs, rental income, part-time work) will change the math.
                </p>
                <p className="pete-caveat rtc-disclaimer">
                  This calculator assumes a more conservative {formatPct(NOMINAL_RETURN_WITHDRAWAL)} return during retirement versus {formatPct(NOMINAL_RETURN_ACCUMULATION)} while saving, reflecting a typical shift to a more conservative portfolio mix after you stop working — but your actual glide path, market conditions, and sequence-of-returns risk will differ.
                </p>
                <p className="pete-caveat rtc-disclaimer">
                  Small changes in assumptions can shift which strategy comes out ahead, by how much, and whether the &ldquo;minimum contribution&rdquo; figure above is realistic for your situation. Treat every number on this page as a directional estimate, not a plan.
                </p>
                <p className="pete-caveat rtc-disclaimer">
                  A comprehensive financial plan replaces these fixed assumptions with your actual numbers — and stress-tests the answer against scenarios this calculator can&apos;t model.
                </p>
              </div>
              <CalculatorDiscoveryCta
                eyebrow={null}
                headline="Want help interpreting your result?"
                body={[
                  'This calculator gives you a signal. The next step is understanding whether that signal changes a real decision about taxes, retirement accounts, or cash flow.',
                  'A comprehensive financial plan can help you compare the result to the tradeoff you are actually trying to make.',
                ]}
                preButtonLine={(
                  <>
                    Most people optimize for retirement.
                    <br />
                    Few optimize for the life between now and retirement.
                    <br />
                    <br />
                    A comprehensive financial plan looks at both.
                  </>
                )}
                postButtonLine="We'll talk through the general ideas around your tax situation, retirement accounts, and withdrawal strategy to help you see which direction may make sense for your household. It's an educational conversation, not a formal plan."
                trustRow={(
                  <p className="rtc-cta-trust-row">
                    <span>🕐 30-minute call</span>
                    <span className="rtc-cta-trust-sep">·</span>
                    <span>📋 No obligation</span>
                    <span className="rtc-cta-trust-sep">·</span>
                    <span>📍 Virtual (Zoom)</span>
                  </p>
                )}
                buttonId="roth_vs_traditional_calculator_financial_checkup_cta"
                secondaryButtonId="roth_vs_traditional_calculator_send_result_cta"
                calculatorId="roth_vs_traditional_calculator"
                calculatorName="roth_analysis"
                className="rtc-cta"
              />
              <ServiceNextStep
                contentCategory={CONTENT_CATEGORIES.TAX}
                calculatorId="roth_vs_traditional_calculator"
                calculatorName="roth_analysis"
                promotionalAngle="roth_calculator"
              />
            </>
          )}
          <p className="pete-footer-note"><Link className="pete-portal-link" to="/links">Back to planning links</Link></p>
        </div>
      </main>
    </div>
  );
}

function Field({ id, fieldName, label, value, setValue, onInteract, step = '1' }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input id={id} type="number" value={value} min="0" step={step} onChange={(event) => { onInteract(fieldName); setValue(event.target.value); }} />
    </div>
  );
}

function BalanceField({ id, fieldName, label, value, setValue, onInteract }) {
  return (
    <div className="pete-field">
      <label htmlFor={id}>{label}</label>
      <input
        id={id}
        type="text"
        inputMode="decimal"
        placeholder="0 or N/A"
        value={value}
        onChange={(event) => { onInteract(fieldName); setValue(event.target.value); }}
      />
    </div>
  );
}

function CalculatedField({ label, value, hint = 'Calculated from your ages' }) {
  return (
    <div className="pete-field rtc-calculated-field">
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{hint}</small>
    </div>
  );
}

function ResultsRow({ name, value, afterTax, idea }) {
  return (
    <div className="rtc-results-row rtc-results-row--compact">
      <div><strong>{name}</strong></div>
      <div>{formatUsd(value)}</div>
      <div>{formatUsd(afterTax)}/yr</div>
      <div>{idea}</div>
    </div>
  );
}

// Legacy exports used by tests or other modules
export function estimateFutureRetirementTaxRate(currentTaxRate, income, filingStatus) {
  const highIncome = filingStatus === 'married' ? 200000 : 100000;
  if (income >= highIncome) return Math.max(0.1, currentTaxRate - 0.04);
  return currentTaxRate;
}

export function calculateTraditionalTaxSavings(contribution, estimatedCurrentTaxRate) {
  return Math.max(0, contribution || 0) * Math.max(0, estimatedCurrentTaxRate || 0);
}

export function calculateContributionSeriesFutureValue(contribution, years, realReturn) {
  if (realReturn === 0) return Math.max(0, contribution || 0) * Math.max(0, years || 0);
  return Math.max(0, contribution || 0) * (((1 + realReturn) ** Math.max(0, years || 0) - 1) / realReturn);
}
